import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserGroupIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import { CountryFlag } from "@/shared/ui";
import { getCountryName } from "@/shared/core";
import type { ClientWithStats } from "../types/client.types";

interface ClientsTableProps {
  clients: ClientWithStats[];
  onView: (clientId: string) => void;
  onEdit: (client: ClientWithStats) => void;
  onDelete?: (clientId: string, clientName: string) => void;
  onToggleStatus?: (clientId: string, currentStatus: string) => void;
  loading?: boolean;
}

type SortField = "name" | "email" | "type" | "status" | "country" | "date";
type SortDirection = "asc" | "desc";

export const ClientsTable: React.FC<ClientsTableProps> = ({
  clients,
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortedClients = [...clients].sort((a, b) => {
    let compareA: string | number | Date;
    let compareB: string | number | Date;

    switch (sortField) {
      case "name":
        compareA = a.profiles?.full_name?.toLowerCase() || "";
        compareB = b.profiles?.full_name?.toLowerCase() || "";
        break;
      case "email":
        compareA = a.profiles?.email?.toLowerCase() || "";
        compareB = b.profiles?.email?.toLowerCase() || "";
        break;
      case "type":
        compareA = a.client_type;
        compareB = b.client_type;
        break;
      case "status":
        compareA = a.status;
        compareB = b.status;
        break;
      case "country":
        compareA = a.profiles?.country || "";
        compareB = b.profiles?.country || "";
        break;
      case "date":
        compareA = new Date(a.lawyer_connection_date || a.created_at);
        compareB = new Date(b.lawyer_connection_date || b.created_at);
        break;
      default:
        return 0;
    }

    if (compareA < compareB) return sortDirection === "asc" ? -1 : 1;
    if (compareA > compareB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-atlantis-100 text-atlantis-800">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            {t("lawyer.clients.status.active")}
          </span>
        );
      case "inactive":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-tiber-100 text-tiber-800">
            <XCircleIcon className="w-4 h-4 mr-1" />
            {t("lawyer.clients.status.inactive")}
          </span>
        );
      case "deleted":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <TrashIcon className="w-4 h-4 mr-1" />
            {t("lawyer.clients.status.deleted")}
          </span>
        );
      default:
        return null;
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? (
      <ChevronUpIcon className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline ml-1" />
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-8 text-center">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-full mb-4"></div>
          <div className="h-8 bg-gray-200 rounded w-full mb-4"></div>
        </div>
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-12 text-center">
        <UserGroupIcon className="h-12 w-12 text-titan mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-juridico mb-2">
          {t("lawyer.clients.empty.title")}
        </h3>
        <p className="text-tiber-700">{t("lawyer.clients.empty.description")}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg border border-tiber-600 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-tiber-200">
          <thead className="bg-romance-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("name")}
              >
                {t("lawyer.clients.table.name")}
                <SortIcon field="name" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("email")}
              >
                {t("lawyer.clients.table.email")}
                <SortIcon field="email" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("type")}
              >
                {t("lawyer.clients.table.type")}
                <SortIcon field="type" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("country")}
              >
                {t("lawyer.clients.table.country")}
                <SortIcon field="country" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-titan uppercase tracking-wider cursor-pointer hover:bg-tiber-100"
                onClick={() => handleSort("status")}
              >
                {t("lawyer.clients.table.status")}
                <SortIcon field="status" />
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-right text-xs font-medium text-titan uppercase tracking-wider"
              >
                {t("lawyer.clients.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-tiber-200">
            {sortedClients.map((client, index) => (
              <motion.tr
                key={client.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="hover:bg-romance-50 transition-colors"
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="shrink-0 h-10 w-10">
                      {client.profiles?.profile_image_url ? (
                        <img
                          className="h-10 w-10 rounded-full"
                          src={client.profiles.profile_image_url}
                          alt={client.profiles.full_name || "Client"}
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center">
                          <span className="text-white font-medium text-sm">
                            {(client.profiles?.full_name || "N/A")
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .toUpperCase()
                              .slice(0, 2)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-juridico">
                        {client.profiles?.full_name || "N/A"}
                      </div>
                      {client.company_name && (
                        <div className="text-sm text-titan">
                          {client.company_name}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-juridico">
                    {client.profiles?.email || "N/A"}
                  </div>
                  {client.phone && (
                    <div className="text-sm text-titan">{client.phone}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-juridico capitalize">
                    {t(
                      `lawyer.clients.type.${client.client_type}`,
                      client.client_type
                    )}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center text-sm text-juridico">
                    {client.profiles?.country && (
                      <>
                        <CountryFlag
                          countryCode={client.profiles.country}
                          size="1.2em"
                          className="mr-2"
                        />
                        {getCountryName(client.profiles.country)}
                      </>
                    )}
                    {!client.profiles?.country && "N/A"}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(client.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onView(client.id)}
                      className="text-tiber-600 hover:text-tiber-900 p-1 rounded hover:bg-tiber-50 transition-colors"
                      title={t("lawyer.clients.actions.view")}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => onEdit(client)}
                      className="text-tiber-600 hover:text-tiber-900 p-1 rounded hover:bg-tiber-50 transition-colors"
                      title={t("lawyer.clients.actions.edit")}
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {client.status !== "deleted" && onToggleStatus && (
                      <button
                        onClick={() =>
                          onToggleStatus?.(client.id, client.status)
                        }
                        className="text-amber-600 hover:text-amber-900 p-1 rounded hover:bg-amber-50 transition-colors"
                        title={
                          client.status === "active"
                            ? t(
                                "lawyer.clients.actions.deactivate",
                                "Deactivate"
                              )
                            : t("lawyer.clients.actions.activate")
                        }
                      >
                        {client.status === "active" ? (
                          <XCircleIcon className="h-5 w-5" />
                        ) : (
                          <CheckCircleIcon className="h-5 w-5" />
                        )}
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() =>
                          onDelete?.(
                            client.id,
                            client.profiles?.full_name || "Client"
                          )
                        }
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                        title={t("lawyer.clients.actions.delete")}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="lg:hidden divide-y divide-tiber-200">
        {sortedClients.map((client, index) => (
          <motion.div
            key={client.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
            className="p-4 hover:bg-romance-50"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <div className="shrink-0 h-12 w-12">
                  {client.profiles?.profile_image_url ? (
                    <img
                      className="h-12 w-12 rounded-full"
                      src={client.profiles.profile_image_url}
                      alt={client.profiles.full_name || "Client"}
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-linear-to-br from-tiber-500 to-tiber-600 flex items-center justify-center">
                      <span className="text-white font-medium">
                        {(client.profiles?.full_name || "N/A")
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <div className="text-sm font-medium text-juridico">
                    {client.profiles?.full_name || "N/A"}
                  </div>
                  <div className="text-sm text-titan">
                    {client.profiles?.email || "N/A"}
                  </div>
                </div>
              </div>
              {getStatusBadge(client.status)}
            </div>
            <div className="flex items-center justify-between text-sm text-titan mb-3">
              <div className="flex items-center">
                {client.profiles?.country && (
                  <>
                    <CountryFlag
                      countryCode={client.profiles.country}
                      size="1em"
                      className="mr-1"
                    />
                    {getCountryName(client.profiles.country)}
                  </>
                )}
                {!client.profiles?.country && "N/A"}
              </div>
              <span className="capitalize">
                {t(
                  `lawyer.clients.type.${client.client_type}`,
                  client.client_type
                )}
              </span>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => onView(client.id)}
                className="flex-1 px-3 py-2 bg-tiber-50 text-tiber-700 rounded-lg hover:bg-tiber-100 transition-colors text-sm font-medium"
              >
                {t("lawyer.clients.actions.view")}
              </button>
              <button
                onClick={() => onEdit(client)}
                className="flex-1 px-3 py-2 bg-tiber-50 text-tiber-700 rounded-lg hover:bg-tiber-100 transition-colors text-sm font-medium"
              >
                {t("lawyer.clients.actions.edit")}
              </button>
              {onDelete && (
                <button
                  onClick={() =>
                    onDelete?.(
                      client.id,
                      client.profiles?.full_name || "Client"
                    )
                  }
                  className="px-3 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
